/**
 * blaster.ts — the mechanics of one blaster shot, as pure functions.
 *
 * Atlas already had the skeleton of a gunfight: the server judges a strike the
 * instant it is fired, and `lib/projectiles.ts` draws a tracer from the muzzle
 * to the mark. What it lacked was the physical coherence that makes a blaster
 * fight read — the shot's timing, its spread, what happens where it lands, and
 * whether the barrel could actually see the target.
 *
 * Everything here is pure and deterministic so the harness can pin it
 * (`verify:blaster`). Rendering, lights and audio live elsewhere.
 *
 * THE TIMING CONTRADICTION. Damage in Atlas is authoritative and instant; the
 * bolt takes time to arrive. Left alone that means the target flinches before
 * the bolt reaches it — the "hitscan plus slow cosmetic bolt" model, and the
 * one arrangement worth avoiding. The fix is not to slow the server down but to
 * hold the VISIBLE reaction until the bolt lands: authority at t=0, every
 * presentation on the bolt's clock. `arrivalMs` is that clock.
 */

/** A 3-vector, structurally compatible with THREE.Vector3 without importing it. */
export interface V3 { x: number; y: number; z: number }

const EPS = 1e-8;

// ─── Travel ────────────────────────────────────────────────────────────────

/**
 * Cinematic bolt speeds (m/s). A blaster bolt is fiction, so these are game
 * rules, not physics: fast enough to feel like a weapon, slow enough to see.
 * At 150 m/s a bolt crosses a 30 m street in 200 ms — visible travel, and a
 * useful lead on a moving target.
 */
export const BOLT_SPEED = { heavy: 80, rifle: 150, carbine: 250, precision: 500 } as const;

/** When a bolt launched now reaches something `distanceM` away, in ms. */
export function arrivalMs(distanceM: number, speedMps: number): number {
  if (!(speedMps > 0)) return 0;
  return (Math.max(0, distanceM) / speedMps) * 1000;
}

// ─── Spread ────────────────────────────────────────────────────────────────

/** Build an orthonormal basis around `d` with a stable fallback axis. */
export function basisFor(d: V3): { t: V3; b: V3 } {
  // Pick the world axis least aligned with d, so the cross product never
  // degenerates — the classic failure is hard-coding +Y and firing straight up.
  const ax = Math.abs(d.x), ay = Math.abs(d.y), az = Math.abs(d.z);
  const up: V3 = ax <= ay && ax <= az ? { x: 1, y: 0, z: 0 } : ay <= az ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 };
  let tx = up.y * d.z - up.z * d.y, ty = up.z * d.x - up.x * d.z, tz = up.x * d.y - up.y * d.x;
  const tl = Math.hypot(tx, ty, tz) || 1;
  tx /= tl; ty /= tl; tz /= tl;
  return {
    t: { x: tx, y: ty, z: tz },
    b: { x: d.y * tz - d.z * ty, y: d.z * tx - d.x * tz, z: d.x * ty - d.y * tx },
  };
}

/**
 * Sample a direction inside a cone of half-angle `halfAngle` around `d`,
 * UNIFORMLY OVER SOLID ANGLE.
 *
 * `u`, `v` are independent samples in [0,1). Sampling yaw and pitch separately
 * — the obvious thing — clusters shots toward the cone's axis and gives a
 * square-ish pattern; this is the standard inversion that does not.
 */
export function sampleSpreadCone(d: V3, halfAngle: number, u: number, v: number): V3 {
  if (!(halfAngle > 0)) return { x: d.x, y: d.y, z: d.z };
  const z = 1 - u * (1 - Math.cos(halfAngle));
  const r = Math.sqrt(Math.max(0, 1 - z * z));
  const phi = 2 * Math.PI * v;
  const { t, b } = basisFor(d);
  const c = Math.cos(phi) * r, s = Math.sin(phi) * r;
  const out = { x: z * d.x + c * t.x + s * b.x, y: z * d.y + c * t.y + s * b.y, z: z * d.z + c * t.z + s * b.z };
  const l = Math.hypot(out.x, out.y, out.z) || 1;
  return { x: out.x / l, y: out.y / l, z: out.z / l };
}

/**
 * The cone half-angle (radians) for a shot, from the shooter's state. Every
 * term is a game-design knob, not a simulation.
 */
export function spreadFor(base: number, s: { moving?: boolean; sprinting?: boolean; crouched?: boolean; aiming?: boolean; heat01?: number; burst?: number }): number {
  let a = base;
  if (s.crouched) a *= 0.6;
  if (s.aiming) a *= 0.45;
  if (s.moving) a *= 1.8;
  if (s.sprinting) a *= 2.6;
  a *= 1 + 0.9 * Math.min(1, Math.max(0, s.heat01 ?? 0));   // a hot barrel wanders
  a *= 1 + 0.12 * Math.min(8, Math.max(0, s.burst ?? 0));    // sustained fire opens up
  return a;
}

// ─── Recoil ────────────────────────────────────────────────────────────────

/**
 * One step of a critically damped return to `target`.
 *
 * The exact solution, not a per-frame lerp: a lerp toward the target is
 * frame-rate dependent, and a spring driven by position alone never settles.
 * `omega` is a recovery rate in inverse seconds.
 */
export function recoverRecoil(x: number, velocity: number, target: number, omega: number, dt: number): { x: number; velocity: number } {
  const offset = x - target;
  const b = velocity + omega * offset;
  const decay = Math.exp(-omega * dt);
  return { x: target + (offset + b * dt) * decay, velocity: (velocity - omega * b * dt) * decay };
}

// ─── Surface geometry ──────────────────────────────────────────────────────

/**
 * How square-on the hit was: 1 straight into the surface, 0 grazing.
 * `d` is the bolt's travel direction, `n` the surface's outward normal.
 */
export function incidence(d: V3, n: V3): number {
  const c = -(d.x * n.x + d.y * n.y + d.z * n.z);
  return c < 0 ? 0 : c > 1 ? 1 : c;
}

/** Mirror `d` about the plane with normal `n`. Exact geometry; whether a bolt ricochets is a rule. */
export function reflect(d: V3, n: V3): V3 {
  const k = 2 * (d.x * n.x + d.y * n.y + d.z * n.z);
  return { x: d.x - k * n.x, y: d.y - k * n.y, z: d.z - k * n.z };
}

/**
 * How much to stretch a mark along the incoming direction at an oblique hit.
 *
 * The natural `1/c` explodes at a graze — a shot that barely kisses a wall
 * would paint a mark hundreds of metres long. Clamped at both ends.
 */
export function decalStretch(c: number, maxStretch = 2.6): number {
  const s = 1 / Math.max(c, 1e-3);
  return s > maxStretch ? maxStretch : s < 1 ? 1 : s;
}

// ─── Cover ─────────────────────────────────────────────────────────────────

/**
 * Can this shot leave the barrel?
 *
 * The camera sits above and behind the gun, so it routinely sees over cover the
 * BARREL is still behind — lean against a crate and the crosshair clears it
 * while the muzzle does not. A ray cast from the camera would happily fire
 * through it. So the muzzle gets its own question, and there are two ways to
 * fail it:
 *
 *   EMBEDDED  the muzzle is inside solid geometry (walked into a wall). A ray
 *             starting inside a solid can report nothing at all — the surface
 *             is behind its origin — so this is tested separately by asking
 *             whether the muzzle is enclosed.
 *   BLOCKED   something solid sits between the muzzle and the aim point,
 *             nearer than the target.
 *
 * `muzzleToObstruction` is the distance to the first solid ahead of the muzzle
 * (null for a clear line); `muzzleToTarget` the distance to what was aimed at.
 */
export function muzzleVerdict(
  muzzleEmbedded: boolean,
  muzzleToObstruction: number | null,
  muzzleToTarget: number,
  clearanceM = 0.12,
): { fires: boolean; why: 'clear' | 'embedded' | 'blocked'; stopAtM: number | null } {
  if (muzzleEmbedded) return { fires: false, why: 'embedded', stopAtM: 0 };
  if (muzzleToObstruction !== null && muzzleToObstruction < muzzleToTarget - clearanceM) {
    // It fires — it just does not get there. The bolt dies on the cover, which
    // is what the player needs to SEE to understand why nothing was hit.
    return { fires: true, why: 'blocked', stopAtM: muzzleToObstruction };
  }
  return { fires: true, why: 'clear', stopAtM: null };
}

// ─── Pass-by ───────────────────────────────────────────────────────────────

/** Where on segment a→b the point `p` is nearest, as a fraction in [0,1]. */
export function closestPointFraction(a: V3, b: V3, p: V3): number {
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
  const len2 = dx * dx + dy * dy + dz * dz;
  if (len2 < EPS) return 0;
  const f = ((p.x - a.x) * dx + (p.y - a.y) * dy + (p.z - a.z) * dz) / len2;
  return f < 0 ? 0 : f > 1 ? 1 : f;
}

/** Nearest approach (m) of the segment a→b to `p`. */
export function passByDistance(a: V3, b: V3, p: V3): number {
  const f = closestPointFraction(a, b, p);
  const qx = a.x + (b.x - a.x) * f, qy = a.y + (b.y - a.y) * f, qz = a.z + (b.z - a.z) * f;
  return Math.hypot(p.x - qx, p.y - qy, p.z - qz);
}

/**
 * Does this bolt earn a snap past the listener's ear?
 *
 * Only if it comes close AND was genuinely going past — a bolt that ends near
 * you because it hit YOU is an impact, not a near miss, and a bolt whose
 * closest approach is at the very start is one you fired yourself.
 */
export function isPassBy(a: V3, b: V3, listener: V3, radiusM = 3): boolean {
  const f = closestPointFraction(a, b, listener);
  if (f <= 0.02 || f >= 0.999) return false;
  return passByDistance(a, b, listener) <= radiusM;
}

// ─── Lead ──────────────────────────────────────────────────────────────────

/**
 * Time for a bolt of speed `s` from the muzzle to intercept a target at
 * relative position `r` moving at relative velocity `u`. Null when there is no
 * intercept (the target outruns the bolt).
 *
 * Solves (u·u − s²)t² + 2(r·u)t + r·r = 0 for the smallest positive root.
 */
export function interceptTime(r: V3, u: V3, s: number): number | null {
  const a = u.x * u.x + u.y * u.y + u.z * u.z - s * s;
  const b = 2 * (r.x * u.x + r.y * u.y + r.z * u.z);
  const c = r.x * r.x + r.y * r.y + r.z * r.z;
  if (Math.abs(a) < 1e-6) {                 // target speed == bolt speed: linear
    if (Math.abs(b) < 1e-9) return null;
    const t = -c / b;
    return t > 0 ? t : null;
  }
  const disc = b * b - 4 * a * c;
  if (disc < 0) return null;
  const root = Math.sqrt(disc);
  const t1 = (-b - root) / (2 * a), t2 = (-b + root) / (2 * a);
  const positives = [t1, t2].filter((t) => t > 0).sort((p, q) => p - q);
  return positives.length ? positives[0] : null;
}

/** Muzzle offsets from the player's own transform (metres). */
export const MUZZLE_UP_M = 1.35;
export const MUZZLE_SIDE_M = 0.22;
export const MUZZLE_FWD_M = 0.45;

/**
 * Where a shot leaves MY BODY when the first-person view model is not the right
 * answer — third person (where the model is hidden and hangs off a camera
 * orbiting up to 8 m BEHIND the avatar, so a bolt from there leaves the
 * viewer's face rather than the gun), or before the model has loaded.
 *
 * Shoulder height, a little right of and ahead of the chest, along the row yaw.
 * `x/y/z` are the player's ABSOLUTE position and `y` is the FEET, so the result
 * is absolute too — the frame the whole shot path works in.
 */
export function bodyMuzzle(yaw: number, x: number, y: number, z: number): V3 {
  const fx = -Math.sin(yaw), fz = -Math.cos(yaw);      // forward (combat_core)
  const rx = Math.cos(yaw), rz = -Math.sin(yaw);        // right = forward turned -90 deg
  return {
    x: x + rx * MUZZLE_SIDE_M + fx * MUZZLE_FWD_M,
    y: y + MUZZLE_UP_M,
    z: z + rz * MUZZLE_SIDE_M + fz * MUZZLE_FWD_M,
  };
}
